/**
 * Microsoft Entra External ID Authentication Service
 *
 * Provides authentication functionality using MSAL.js for External ID.
 *
 * Authentication Flow:
 * 1. User clicks "Sign In / Sign Up" button
 * 2. App redirects to External ID's hosted login page
 * 3. User authenticates (email/password, Google, Facebook, or Microsoft)
 * 4. External ID redirects back with ID token
 * 5. App syncs user to backend database
 *
 * Token Refresh Flow:
 * - Tokens are refreshed silently using acquireTokenSilent() before API calls
 * - If silent refresh fails (e.g., session expired), user is redirected to login
 * - Auth expiration events notify the app to update UI state
 *
 * Note: We use ID tokens (not access tokens) for API authentication because
 * External ID local accounts cannot obtain access tokens for custom API scopes.
 */

import { PublicClientApplication, InteractionRequiredAuthError } from '@azure/msal-browser';
import type {
  AuthenticationResult,
  AccountInfo,
  RedirectRequest,
  SilentRequest
} from '@azure/msal-browser';
import type { B2CConfig } from '../config/authConfig';
import { createMsalConfig, LOGIN_SCOPES } from '../config/authConfig';
import type { AuthFlow } from '../constants/auth';
import { AccountErrorCodes } from '../constants/accountErrorCodes';
import {
  getAccountDisabledErrorCode,
  stashAccountDisabledBanner,
} from './accountDisabledFlow';

/**
 * Thrown when GET /auth/me returns 403 account_disabled (PBI #1230).
 * Callers must not fall back to MSAL token claims.
 */
export class AccountDisabledError extends Error {
  constructor(message: string) {
    super(message);
    this.name = 'AccountDisabledError';
  }
}

/**
 * Callback type for auth expiration events.
 * Called when the session expires and user needs to re-authenticate.
 */
export type AuthExpirationCallback = () => void;

/**
 * Options for {@link B2CAuthService.logout}.
 */
export interface B2CLogoutOptions {
  /**
   * When true (default), ends the Microsoft Entra browser session via
   * `logoutRedirect` so the next sign-in or sign-up is not silent SSO.
   * Set false for internal cleanup (e.g. invalid token during init) where a
   * full IdP redirect would be disruptive.
   */
  signOutFromIdp?: boolean;
}

/**
 * User information extracted from External ID token
 */
export interface B2CUserInfo {
  id: string;
  email: string;
  name: string | null;
}

/**
 * External ID Authentication Service Class
 * Handles all authentication operations with Microsoft Entra External ID.
 * Includes token refresh logic and auth expiration event handling.
 */
export class B2CAuthService {
  private msalInstance: PublicClientApplication | null = null;
  private b2cConfig: B2CConfig | null = null;
  private initialized: boolean = false;

  /** Callback invoked when auth session expires and user needs to re-login */
  private authExpirationCallback: AuthExpirationCallback | null = null;

  /** Flag to prevent multiple simultaneous token refresh attempts */
  private isRefreshing: boolean = false;

  /** Promise for pending token refresh - allows multiple callers to await same refresh */
  private refreshPromise: Promise<string | null> | null = null;

  /**
   * Promise for in-progress initialization.
   *
   * Acts as a mutex so that concurrent callers (e.g. React Strict Mode's
   * double-invocation of effects) share a single initialization flow rather
   * than racing to call msalInstance.initialize() / handleRedirectPromise()
   * simultaneously. The second caller simply awaits the same promise instead
   * of starting a new one, which would call handleRedirectPromise() on a
   * not-yet-initialized PublicClientApplication and throw
   * BrowserAuthError: uninitialized_public_client_application.
   */
  private initializePromise: Promise<B2CUserInfo | null> | null = null;

  /**
   * Initialize the MSAL instance with External ID configuration.
   * Processes any pending redirect from login flow.
   *
   * Concurrent calls (e.g. React Strict Mode double-invoke) are serialized:
   * the second caller awaits the same promise as the first rather than
   * triggering a parallel MSAL init.
   *
   * @param config External ID configuration from backend
   * @returns Authenticated user if redirect was processed, null otherwise
   */
  async initialize(config: B2CConfig): Promise<B2CUserInfo | null> {
    // If already fully initialized with the same config, just process the
    // redirect in case we're returning from a login flow on a page reload.
    if (this.initialized && this.b2cConfig?.clientId === config.clientId && this.msalInstance) {
      return await this.processRedirectIfNeeded();
    }

    // If initialization is already in progress (e.g. React Strict Mode
    // double-invokes the effect), wait for the existing promise rather than
    // starting a second MSAL init that would race with the first.
    if (this.initializePromise) {
      return this.initializePromise;
    }

    // Start initialization and store the promise so concurrent callers can share it
    this.initializePromise = this._doInitialize(config).finally(() => {
      this.initializePromise = null;
    });

    return this.initializePromise;
  }

  /**
   * Internal initialization implementation.
   * Always creates (or recreates) the MSAL instance and awaits its
   * built-in initialize() before calling handleRedirectPromise().
   */
  private async _doInitialize(config: B2CConfig): Promise<B2CUserInfo | null> {
    this.b2cConfig = config;
    const msalConfig = createMsalConfig(config);

    this.msalInstance = new PublicClientApplication(msalConfig);

    // MSAL requires initialize() to be awaited before any other API call
    await this.msalInstance.initialize();

    // Handle redirect promise (for returning from External ID login)
    const user = await this.processRedirectIfNeeded();

    this.initialized = true;
    return user;
  }

  /**
   * Process the redirect callback if we're returning from a login redirect.
   *
   * @returns Authenticated user if redirect was processed successfully, null otherwise
   */
  private async processRedirectIfNeeded(): Promise<B2CUserInfo | null> {
    if (!this.msalInstance) {
      return null;
    }

    try {
      // navigateToLoginRequestUrl: false — opt out of MSAL's default
      // post-login navigation to the URL the user was on before login.
      // The /auth/callback page synchronizes the user to our backend and
      // then React Router takes over routing; if MSAL also navigated, the
      // two would race. In MSAL v4 this was a global auth-config option;
      // v5 moved it onto handleRedirectPromise's HandleRedirectPromiseOptions.
      const redirectResult = await this.msalInstance.handleRedirectPromise({
        navigateToLoginRequestUrl: false,
      });

      // If we got a result from redirect, sync the user to the backend database
      if (redirectResult && redirectResult.account) {
        const user = await this.syncUserToBackend(redirectResult);
        return user;
      }
    } catch (error: unknown) {
      console.error('[B2CAuthService] Error processing redirect:', error);

      // Check for interaction_in_progress error - this means there's a stuck interaction
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes('interaction_in_progress')) {
        // Clear the interaction state to recover
        try {
          // MSAL stores interaction state in sessionStorage with keys starting with 'msal.'
          const keysToRemove: string[] = [];
          for (let i = 0; i < sessionStorage.length; i++) {
            const key = sessionStorage.key(i);
            if (key && key.startsWith('msal.')) {
              keysToRemove.push(key);
            }
          }
          keysToRemove.forEach((key) => sessionStorage.removeItem(key));
        } catch (clearError) {
          console.error('[B2CAuthService] Failed to clear storage:', clearError);
        }
      }

      // Clear the error from URL if present
      if (window.location.hash.includes('error') || window.location.search.includes('error')) {
        window.history.replaceState({}, document.title, window.location.pathname);
      }
    }

    return null;
  }

  /**
   * Sync user to backend database after successful authentication.
   * Creates/updates the user record in our local database.
   *
   * Uses ID token for API calls. In External ID tenants where local accounts
   * cannot obtain access tokens for the API (AADSTS500207), requesting API
   * scopes breaks login; we therefore use ID token only so login works in all environments.
   * App roles appear only in access tokens, so admin portal visibility may require
   * a tenant where the frontend is allowed to request API scopes (e.g. deployed dev/prod).
   */
  private async syncUserToBackend(authResult: AuthenticationResult): Promise<B2CUserInfo> {
    // Set the active account first
    if (authResult.account) {
      this.msalInstance?.setActiveAccount(authResult.account);
    }

    const token = authResult.idToken || authResult.accessToken;
    if (token) {
      localStorage.setItem('token', token);
    }

    // Call /api/auth/me to sync user to database
    // Uses fetch directly to avoid circular dependency with apiClient
    try {
      const baseUrl = import.meta.env.VITE_API_URL || '/api';
      const response = await fetch(`${baseUrl}/auth/me`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        credentials: 'include',
      });

      if (!response.ok) {
        if (response.status === 403) {
          let body: { errorCode?: string; ErrorCode?: string; message?: string; Message?: string } =
            {};
          try {
            body = (await response.json()) as typeof body;
          } catch {
            // Ignore JSON parse errors — fall through to generic throw.
          }

          if (getAccountDisabledErrorCode(body) === AccountErrorCodes.AccountDisabled) {
            const message =
              body.message ??
              body.Message ??
              'Your account has been disabled. If you believe this is a mistake, contact support.';
            stashAccountDisabledBanner(message);
            throw new AccountDisabledError(message);
          }
        }

        throw new Error(`HTTP ${response.status}`);
      }

      const user: B2CUserInfo = await response.json();
      localStorage.setItem('user', JSON.stringify(user));
      return user;
    } catch (error) {
      if (error instanceof AccountDisabledError) {
        throw error;
      }

      console.warn('Failed to sync user to backend:', error);
      throw error;
    }
  }

  /**
   * Check if MSAL is initialized
   */
  isInitialized(): boolean {
    return this.initialized && this.msalInstance !== null;
  }

  /**
   * Get the current authenticated account from MSAL.
   * Returns null when MSAL is missing or not yet initialized — callers (including
   * the apiClient request interceptor) must not throw during that race, or public
   * pages like /pricing fail to load AllowAnonymous data on hard refresh.
   */
  getActiveAccount(): AccountInfo | null {
    if (!this.msalInstance || !this.initialized) return null;

    try {
      const accounts = this.msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        this.msalInstance.setActiveAccount(accounts[0]);
        return accounts[0];
      }
    } catch (error) {
      // MSAL throws BrowserAuthError: uninitialized_public_client_application
      // if getAllAccounts runs before initialize() completes.
      console.warn('[B2CAuthService] getActiveAccount skipped — MSAL not ready', error);
    }

    return null;
  }

  /**
   * Initiate login by redirecting to External ID's hosted login page.
   * User can sign in with email/password or social providers (Google, etc.)
   * External ID handles all authentication flows.
   *
   * @param flow Optional auth flow type:
   *   - 'signin': Standard sign-in for existing users (default)
   *   - 'signup': Hints to External ID to show account creation
   *
   * Business Rule:
   * - For 'signup' flow, we use prompt='create' which hints to External ID
   *   to show the account creation UI directly. If not supported by the
   *   tenant configuration, it falls back to the combined sign-in/sign-up page.
   */
  async login(flow: AuthFlow = 'signin'): Promise<void> {
    if (!this.msalInstance) {
      throw new Error('MSAL instance not initialized. Please wait for authentication to initialize.');
    }

    if (!this.b2cConfig) {
      throw new Error('B2C configuration not available.');
    }

    // Reinitialize if needed (after logout)
    if (!this.initialized) {
      await this.msalInstance.initialize();
      this.initialized = true;
    }

    // Use pre-constructed authority from config
    const authority = this.b2cConfig.authority || `${this.b2cConfig.instance}/${this.b2cConfig.domain}`;

    // Build the login request with appropriate prompt based on flow.
    // Use only LOGIN_SCOPES (OIDC); never add b2cConfig.apiScopes or API scopes
    // or Entra may return AADSTS500207 for local accounts.
    // - 'signin' flow: prompt='login' forces credential entry
    // - 'signup' flow: prompt='create' hints at account creation (Entra External ID)
    const loginRequest: RedirectRequest = {
      scopes: [...LOGIN_SCOPES],
      authority: authority,
      prompt: flow === 'signup' ? 'create' : 'login',
    };

    // Redirect to External ID's hosted login page
    await this.msalInstance.loginRedirect(loginRequest);
  }

  /**
   * Clears MSAL interaction state from sessionStorage (interaction_in_progress, etc.).
   */
  private clearMsalSessionStorage(): void {
    try {
      const keysToRemove: string[] = [];
      for (let i = 0; i < sessionStorage.length; i++) {
        const key = sessionStorage.key(i);
        if (key && key.startsWith('msal.')) {
          keysToRemove.push(key);
        }
      }
      keysToRemove.forEach((key) => sessionStorage.removeItem(key));
    } catch (error) {
      console.warn('[B2CAuthService] Failed to clear MSAL sessionStorage', error);
    }
  }

  /**
   * Removes MSAL token cache keys from localStorage.
   */
  private clearMsalLocalStorage(): void {
    Object.keys(localStorage).forEach((key) => {
      if (key.startsWith('msal.') || key.includes('.login.') || key.includes('.request.')) {
        localStorage.removeItem(key);
      }
    });
  }

  /**
   * Logout the user.
   *
   * Clears app tokens, MSAL cache, and (by default) the Entra browser session
   * via `logoutRedirect`. Without the IdP sign-out, a "Start for Free" /
   * sign-up click immediately after logout can silently SSO the same account
   * back in because only local MSAL cache was cleared.
   *
   * @returns `true` when `logoutRedirect` was started (browser navigates away;
   *          callers must not client-navigate afterward).
   */
  async logout(options?: B2CLogoutOptions): Promise<boolean> {
    const signOutFromIdp = options?.signOutFromIdp ?? true;

    // Clear app-owned storage first so no stale JWT survives the redirect.
    localStorage.removeItem('token');
    localStorage.removeItem('refreshToken');
    localStorage.removeItem('user');

    this.clearMsalSessionStorage();

    if (!this.msalInstance) {
      this.clearMsalLocalStorage();
      this.initialized = false;
      return false;
    }

    const accounts = this.msalInstance.getAllAccounts();

    if (signOutFromIdp && accounts.length > 0) {
      if (!this.initialized) {
        await this.msalInstance.initialize();
        this.initialized = true;
      }

      // End the Entra session; user returns to postLogoutRedirectUri (site origin).
      await this.msalInstance.logoutRedirect({
        account: accounts[0],
        postLogoutRedirectUri: window.location.origin,
      });
      return true;
    }

    for (const account of accounts) {
      await this.msalInstance.clearCache({
        account,
      });
    }

    this.clearMsalLocalStorage();
    this.initialized = false;
    return false;
  }

  /**
   * Get the current user from storage or MSAL account
   */
  getCurrentUser(): B2CUserInfo | null {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      return JSON.parse(userStr);
    }

    const account = this.getActiveAccount();
    if (account) {
      return {
        id: account.localAccountId,
        email: account.username,
        name: account.name || null,
      };
    }

    return null;
  }

  /**
   * Check if user is authenticated (has valid token or MSAL account)
   */
  isAuthenticated(): boolean {
    return !!localStorage.getItem('token') || !!this.getActiveAccount();
  }

  /**
   * Register a callback to be invoked when auth session expires.
   * This allows the AuthContext to be notified and update UI state.
   *
   * @param callback Function to call when session expires
   */
  onAuthExpiration(callback: AuthExpirationCallback): void {
    this.authExpirationCallback = callback;
  }

  /**
   * Unregister the auth expiration callback.
   * Called during cleanup or logout.
   */
  clearAuthExpirationCallback(): void {
    this.authExpirationCallback = null;
  }

  /**
   * Handle definitive authentication failure.
   * Called by the API client when a retried request (after token refresh) still
   * receives 401, meaning the session is truly expired. Clears stored tokens
   * and triggers the auth expiration callback to redirect the user to login.
   */
  handleAuthFailure(): void {
    localStorage.removeItem('token');

    if (this.authExpirationCallback) {
      console.warn('[B2CAuthService] Triggering auth expiration after retry failure');
      this.authExpirationCallback();
    }
  }

  /**
   * Attempt to silently acquire a fresh token using MSAL.
   * Uses cached refresh token to get a new ID token without user interaction.
   *
   * @param forceRefresh When true, bypasses MSAL cache and goes to the identity provider.
   *   Must be true when called after a 401 or proactive near-expiry refresh, otherwise MSAL
   *   may return the same stale cached token that the server already rejected.
   * @returns Fresh ID token if successful, null if interaction required
   * @throws Error if MSAL is not initialized
   */
  async acquireTokenSilent(forceRefresh: boolean = false): Promise<string | null> {
    if (!this.msalInstance) {
      console.warn('[B2CAuthService] MSAL not initialized, cannot refresh token');
      return null;
    }

    if (!this.b2cConfig) {
      console.warn('[B2CAuthService] B2C config not available');
      return null;
    }

    const account = this.getActiveAccount();
    if (!account) {
      console.warn('[B2CAuthService] No active account for token refresh');
      return null;
    }

    try {
      const authority = this.b2cConfig.authority || `${this.b2cConfig.instance}/${this.b2cConfig.domain}`;

      // Use only OIDC scopes; requesting API scopes can cause AADSTS500207 for some account types
      const silentRequest: SilentRequest = {
        scopes: [...LOGIN_SCOPES],
        account: account,
        authority: authority,
        forceRefresh,
      };

      const response: AuthenticationResult = await this.msalInstance.acquireTokenSilent(silentRequest);

      if (response.idToken) {
        localStorage.setItem('token', response.idToken);
        return response.idToken;
      }
      if (response.accessToken) {
        localStorage.setItem('token', response.accessToken);
        return response.accessToken;
      }

      return null;
    } catch (error: unknown) {
      // InteractionRequiredAuthError means user needs to re-authenticate
      if (error instanceof InteractionRequiredAuthError) {
        console.warn('[B2CAuthService] Interaction required - session expired');
        return null;
      }

      console.error('[B2CAuthService] Silent token acquisition failed:', error);
      return null;
    }
  }

  /**
   * Attempt to refresh the token. If already refreshing, returns the pending promise.
   * This prevents multiple simultaneous refresh attempts.
   *
   * Business Logic:
   * - If token refresh succeeds, returns the new token
   * - If token refresh fails due to expired session, triggers auth expiration callback
   * - Multiple callers will share the same refresh promise to avoid race conditions
   *
   * @returns Fresh token if successful, null if refresh failed
   */
  async refreshToken(): Promise<string | null> {
    // If already refreshing, return the pending promise so concurrent callers
    // don't each trigger their own network request. Only log once per batch.
    if (this.isRefreshing && this.refreshPromise) {
      return this.refreshPromise;
    }

    this.isRefreshing = true;

    // Create a new refresh promise.
    // Always force MSAL to go to the identity provider rather than returning
    // its cached token. This method is called when the current token is known
    // to be near-expiry or already rejected by the server (401), so returning
    // the same cached token would cause a retry loop.
    this.refreshPromise = (async (): Promise<string | null> => {
      try {
        const newToken = await this.acquireTokenSilent(true);

        if (newToken) {
          return newToken;
        }

        // Token refresh failed - session has expired
        console.warn('[B2CAuthService] Token refresh failed, session expired');

        // Notify listeners that auth has expired.
        if (this.authExpirationCallback) {
          console.warn('[B2CAuthService] Triggering auth expiration callback');
          this.authExpirationCallback();
        }

        return null;
      } finally {
        this.isRefreshing = false;
        this.refreshPromise = null;
      }
    })();

    return this.refreshPromise;
  }

  /**
   * Get the current token, refreshing if necessary.
   * This is the primary method for getting a valid token for API calls.
   *
   * Never throws: public pages call apiClient before MSAL initialize finishes
   * (hard refresh). Rejecting the interceptor would drop AllowAnonymous fetches
   * such as GET /subscription/plans on /pricing.
   *
   * @param forceRefresh If true, forces a token refresh even if current token exists
   * @returns Valid token or null if authentication required / MSAL not ready
   */
  async getValidToken(forceRefresh: boolean = false): Promise<string | null> {
    try {
      return await this.getValidTokenInner(forceRefresh);
    } catch (error) {
      console.warn('[B2CAuthService] getValidToken failed; continuing without token', error);
      return null;
    }
  }

  /**
   * Internal token resolution used by {@link getValidToken}.
   */
  private async getValidTokenInner(forceRefresh: boolean = false): Promise<string | null> {
    const currentToken = localStorage.getItem('token');

    if (!currentToken) {
      // Unauthenticated: do not call refreshToken() when there is no MSAL account
      // yet. During /auth/callback, handleRedirectPromise may still be in flight;
      // the axios request interceptor will call getValidToken before localStorage
      // has a token. refreshToken() → acquireTokenSilent() with no account returns
      // null and (incorrectly) triggered the auth expiration callback, clearing
      // storage and aborting the login—same end state as a harsh logout.
      if (!this.getActiveAccount()) {
        return null;
      }
      // Token key missing but MSAL still has an account (e.g. eviction, partial
      // storage wipe) — try silent refresh only in that case.
      return await this.refreshToken();
    }

    if (forceRefresh) {
      return await this.refreshToken();
    }

    // Check if token is about to expire (within 5 minutes)
    // ID tokens from External ID are typically valid for 1 hour
    try {
      const tokenParts = currentToken.split('.');
      if (tokenParts.length === 3) {
        const payload = JSON.parse(atob(tokenParts[1]));
        const expiresAt = payload.exp * 1000; // Convert to milliseconds
        const now = Date.now();

        // 5 minute buffer before expiry
        const REFRESH_BUFFER_MS = 5 * 60 * 1000;

        if (expiresAt - now < REFRESH_BUFFER_MS) {
          // Only log once; concurrent callers will each reach this point but
          // refreshToken() deduplicates the actual network request.
          if (!this.isRefreshing) {
            console.log('[B2CAuthService] Token expires soon, refreshing proactively');
          }
          return await this.refreshToken();
        }
      }
    } catch (e) {
      // Token parsing failed, try to refresh
      console.warn('[B2CAuthService] Token parsing failed, attempting refresh');
      return await this.refreshToken();
    }

    return currentToken;
  }
}

// Export singleton instance
export const b2cAuthService = new B2CAuthService();
